import React, { useState } from 'react';
import { useDarkMode } from '../../context/DarkModeContext';
import { useConsent } from '../../context/ConsentContext';
import { createLogger } from '../../utils/logger';
import { 
  IoShieldCheckmarkOutline, 
  IoTimeOutline, 
  IoDocumentTextOutline,
  IoSaveOutline,
  IoCheckmarkCircleOutline
} from 'react-icons/io5';
import type { ConsentPreferences } from '../../types/privacy';
import { ConsentControls } from './ConsentControls';
import { ConsentHistory } from './ConsentHistory';
import { DataRightsPortal } from './DataRightsPortal';

const logger = createLogger({ module: 'PrivacyDashboard' });

interface PrivacyDashboardProps {
  className?: string;
}

export const PrivacyDashboard: React.FC<PrivacyDashboardProps> = ({
  className = ''
}) => {
  const { darkMode } = useDarkMode();
  const { consent, updateConsent, isLoading } = useConsent();
  const [localConsent, setLocalConsent] = useState<ConsentPreferences>(consent);
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Update local state when consent context changes
  React.useEffect(() => {
    setLocalConsent(consent);
    setHasChanges(false);
  }, [consent]);

  const handleConsentChange = (category: keyof ConsentPreferences) => {
    if (category === 'necessary') return; // Cannot disable necessary consent
    
    const newConsent = {
      ...localConsent,
      [category]: !localConsent[category]
    };
    
    setLocalConsent(newConsent);
    
    // Check if there are changes compared to the original consent
    const hasActualChanges = Object.keys(newConsent).some(
      key => newConsent[key as keyof ConsentPreferences] !== consent[key as keyof ConsentPreferences]
    );
    
    setHasChanges(hasActualChanges);
    setError(null);
    setSaveSuccess(false);
  };

  const handleSave = async () => {
    if (!hasChanges) return;

    try {
      setSaving(true);
      setError(null);
      setSaveSuccess(false);

      const success = await updateConsent(localConsent);
      
      if (success) {
        setHasChanges(false);
        setSaveSuccess(true);
        logger.info('Privacy preferences updated successfully');
        
        // Clear success message after 3 seconds
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        throw new Error('Failed to update privacy preferences');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to save preferences';
      setError(errorMessage);
      logger.error('Error saving privacy preferences', err);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setLocalConsent(consent);
    setHasChanges(false);
    setError(null);
    setSaveSuccess(false);
  };

  const getConsentSummary = () => {
    const enabledCount = Object.values(localConsent).filter(Boolean).length;
    const totalCount = Object.keys(localConsent).length;
    return { enabledCount, totalCount };
  };

  const { enabledCount, totalCount } = getConsentSummary();

  if (isLoading) {
    return (
      <div className={`p-8 rounded-lg border ${
        darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'
      } ${className}`}>
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          <span className={`ml-3 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            Loading privacy settings...
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header */}
      <div className={`p-6 rounded-lg border ${
        darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'
      }`}>
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center mb-2">
              <IoShieldCheckmarkOutline className="w-6 h-6 text-blue-500 mr-3" />
              <h1 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Privacy Dashboard
              </h1>
            </div>
            <p className={`${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Manage your privacy preferences and data rights
            </p>
          </div>
          
          <div className="text-right">
            <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Consent Status
            </div>
            <div className={`text-lg font-semibold ${
              enabledCount === totalCount 
                ? 'text-green-500' 
                : enabledCount === 1 
                  ? 'text-yellow-500' 
                  : 'text-blue-500'
            }`}>
              {enabledCount} of {totalCount} enabled
            </div>
          </div>
        </div>
      </div>

      {/* Privacy Preferences */}
      <div className={`p-6 rounded-lg border ${
        darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'
      }`}>
        <div className="flex items-center justify-between mb-4">
          <h2 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Privacy Preferences
          </h2>
          
          {hasChanges && (
            <div className="flex items-center space-x-2">
              <button
                onClick={handleReset}
                className={`px-3 py-1 text-sm rounded border ${
                  darkMode 
                    ? 'border-gray-600 text-gray-300 hover:bg-gray-700' 
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                Reset
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <IoSaveOutline className="w-4 h-4 mr-2" />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          )}
        </div>

        {/* Success Message */}
        {saveSuccess && (
          <div className={`mb-4 p-3 rounded-md border ${
            darkMode ? 'border-green-700 bg-green-900/20' : 'border-green-200 bg-green-50'
          }`}>
            <div className="flex items-center">
              <IoCheckmarkCircleOutline className="w-5 h-5 text-green-500 mr-2" />
              <p className={`text-sm ${darkMode ? 'text-green-300' : 'text-green-800'}`}>
                Privacy preferences updated successfully!
              </p>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className={`mb-4 p-3 rounded-md border ${
            darkMode ? 'border-red-700 bg-red-900/20' : 'border-red-200 bg-red-50'
          }`}>
            <div className="flex items-center">
              <IoShieldCheckmarkOutline className="w-5 h-5 text-red-500 mr-2" />
              <p className={`text-sm ${darkMode ? 'text-red-300' : 'text-red-800'}`}>
                {error}
              </p>
            </div>
          </div>
        )}

        <p className={`text-sm mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          Control how your data is used to improve your experience. You can change these settings at any time.
        </p>

        <ConsentControls
          consent={localConsent}
          onConsentChange={handleConsentChange}
          disabled={saving}
          showDescriptions={true}
          showExamples={true}
        />

        {hasChanges && (
          <div className={`mt-4 p-3 rounded-md border ${
            darkMode ? 'border-yellow-700 bg-yellow-900/20' : 'border-yellow-200 bg-yellow-50'
          }`}>
            <p className={`text-sm ${darkMode ? 'text-yellow-300' : 'text-yellow-800'}`}>
              You have unsaved changes. Click "Save Changes" to apply your new preferences.
            </p>
          </div>
        )}
      </div>

      {/* Consent History */}
      <div className={`rounded-lg border ${
        darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'
      }`}>
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center">
            <IoTimeOutline className="w-5 h-5 text-gray-500 mr-2" />
            <h2 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Consent History
            </h2>
          </div>
          <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            View all changes to your privacy preferences
          </p>
        </div>
        <ConsentHistory className="border-0 bg-transparent" />
      </div>

      {/* Data Rights Portal */}
      <div className={`rounded-lg border ${
        darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'
      }`}>
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center">
            <IoDocumentTextOutline className="w-5 h-5 text-gray-500 mr-2" />
            <h2 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Data Rights
            </h2>
          </div>
          <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Export your data or delete your account
          </p>
        </div>
        <DataRightsPortal className="border-0 bg-transparent" />
      </div>

      {/* Footer Information */}
      <div className={`p-4 rounded-lg border ${
        darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'
      }`}>
        <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          <p className="mb-2">
            <strong>Your Privacy Rights:</strong> Under GDPR and CCPA, you have the right to access, 
            correct, delete, or export your personal data. You can also object to or restrict certain 
            processing activities.
          </p>
          <p>
            For questions about our privacy practices, please contact us at{' '}
            <a href="mailto:privacy@careercard.app" className="text-blue-500 hover:underline">
              privacy@careercard.app
            </a>
            {' '}or review our{' '}
            <a href="/privacy" className="text-blue-500 hover:underline">
              Privacy Policy
            </a>
            .
          </p>
        </div>
      </div>
    </div>
  );
}; 